import type { ReactNode } from 'react';
import { cx } from './cx';
import { Icon } from './basics';
import type { IconName } from './icons';
import { Amount, formatAmount, type MoneyDirection } from './money';

const ROW_GLYPHS: Record<NonNullable<RecordRowProps['kind']>, IconName> = {
  sale: 'arrow-down-left',
  expense: 'arrow-up-right',
  stock: 'package',
  tax: 'landmark',
  invoice: 'file-text',
  transfer: 'wallet',
};

export interface RecordRowProps {
  kind?: 'sale' | 'expense' | 'stock' | 'tax' | 'invoice' | 'transfer';
  title: string;
  meta?: ReactNode;
  /** Kobo. */
  minor?: number;
  direction?: MoneyDirection;
  source?: 'whatsapp' | 'app';
  status?: ReactNode;
  /** Out of the books: muted, amount struck through (spec 3A §7.4). */
  removed?: boolean;
  onClick?: () => void;
  className?: string;
}

/** One entry in a list. 64 px; the whole row is the tap target. */
export function RecordRow({ kind = 'sale', title, meta, minor, direction, source, status, removed, onClick, className }: RecordRowProps) {
  const dir = direction ?? (kind === 'sale' ? 'in' : kind === 'expense' ? 'out' : 'neutral');
  const body = (
    <>
      <span className={cx('zb-row__glyph', `zb-row__glyph--${kind}`)}>
        <Icon name={ROW_GLYPHS[kind]} size={22} />
      </span>
      <span className="zb-row__main">
        <span className="zb-row__title">{title}</span>
        <span className="zb-row__meta">
          {source === 'whatsapp' && (
            <span className="zb-row__src">
              <Icon name="message-circle" size={14} strokeWidth={2.5} />
              WhatsApp
            </span>
          )}
          {meta}
        </span>
      </span>
      <span className="zb-row__end">
        {minor !== undefined && <Amount minor={minor} direction={dir} />}
        {status}
      </span>
    </>
  );
  const classes = cx('zb-row', onClick && 'zb-row--tap', removed && 'zb-row--removed', className);
  return onClick ? (
    <button type="button" onClick={onClick} className={classes}>
      {body}
    </button>
  ) : (
    <div className={classes}>{body}</div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export interface CustomerRowProps {
  name: string;
  meta?: ReactNode;
  /** Kobo owed. */
  owedMinor?: number;
  status?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function CustomerRow({ name, meta, owedMinor, status, onClick, className }: CustomerRowProps) {
  const body = (
    <>
      <span className="zb-avatar" aria-hidden="true">
        {initials(name)}
      </span>
      <span className="zb-row__main">
        <span className="zb-row__title">{name}</span>
        {meta && <span className="zb-row__meta">{meta}</span>}
      </span>
      <span className="zb-row__end">
        {owedMinor ? <Amount minor={owedMinor} direction="owed" /> : null}
        {status}
      </span>
    </>
  );
  const classes = cx('zb-row', onClick && 'zb-row--tap', className);
  return onClick ? (
    <button type="button" onClick={onClick} className={classes}>
      {body}
    </button>
  ) : (
    <div className={classes}>{body}</div>
  );
}

export interface AgentNoteProps {
  children: ReactNode;
  time?: string;
  channel?: string;
  action?: string;
  onAction?: () => void;
  className?: string;
}

/**
 * Something the agent did or said, shown in the app (alerts, capture
 * replies). It signs as Zibanu, the institution — never the person's own
 * agent name, which is a secret stored only hashed.
 */
export function AgentNote({ children, time, channel = 'WhatsApp', action, onAction, className }: AgentNoteProps) {
  return (
    <div className={cx('zb-agent', className)}>
      <span className="zb-agent__mark" aria-hidden="true">
        Z
      </span>
      <div className="zb-agent__body">
        <div className="zb-agent__meta">
          <strong>Zibanu</strong>
          <span>· via {channel}</span>
          {time && <span>· {time}</span>}
        </div>
        <div className="zb-agent__text">{children}</div>
        {action && (
          <button type="button" className="zb-link-btn" onClick={onAction}>
            {action}
          </button>
        )}
      </div>
    </div>
  );
}

export interface SummaryRow {
  label: string;
  /** Kobo (shown as money) or anything else. */
  value: number | ReactNode;
  hint?: string;
  direction?: MoneyDirection;
  flag?: boolean;
  /** Makes the fact tappable ("Fix this": tap what's wrong). */
  onSelect?: () => void;
}

export interface SummaryListProps {
  rows: SummaryRow[];
  total?: { label: string; minor: number; hint?: string; direction?: MoneyDirection };
  className?: string;
}

const shown = (row: SummaryRow) =>
  typeof row.value === 'number' ? <Amount minor={row.value} direction={row.direction ?? 'neutral'} size="sm" /> : row.value;

/** Facts as a list. When any fact is tappable, every row is a button that names what it changes. */
export function SummaryList({ rows, total, className }: SummaryListProps) {
  if (rows.some((r) => r.onSelect)) {
    return (
      <div className={cx('zb-sum', 'zb-sum--tap', className)}>
        {rows.map((row) => (
          <button
            key={row.label}
            type="button"
            className={cx('zb-sum__row', row.flag && 'zb-sum__row--flag')}
            onClick={row.onSelect}
            disabled={!row.onSelect}
            aria-label={`${row.label}: ${typeof row.value === 'number' ? formatAmount(row.value) : typeof row.value === 'string' ? row.value : ''}`}
          >
            <span className="zb-sum__k">
              <span className="zb-sum__label">{row.label}</span>
              {row.hint && <span className="zb-sum__hint">{row.hint}</span>}
            </span>
            <span className="zb-sum__v">
              {shown(row)}
              {row.onSelect && <Icon name="chevron-right" size={20} />}
            </span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <dl className={cx('zb-sum', className)}>
      {rows.map((row) => (
        <div key={row.label} className={cx('zb-sum__row', row.flag && 'zb-sum__row--flag')}>
          <dt>
            <span className="zb-sum__label">{row.label}</span>
            {row.hint && <span className="zb-sum__hint">{row.hint}</span>}
          </dt>
          <dd>{shown(row)}</dd>
        </div>
      ))}
      {total && (
        <div className="zb-sum__row zb-sum__row--total">
          <dt>
            <span className="zb-sum__label">{total.label}</span>
            {total.hint && <span className="zb-sum__hint">{total.hint}</span>}
          </dt>
          <dd>
            <Amount minor={total.minor} direction={total.direction ?? 'neutral'} size="lg" showSign={false} />
          </dd>
        </div>
      )}
    </dl>
  );
}
